import { BLEScanOptions } from '@ionic-native/ble';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import {
  BLE_CHARACTERISTIC_RSP,
  BLE_SERVICE_SPINNAX,
  NAME_FREAK,
} from '../../../spinnaxBase/constants';
import * as Pck from '../../../spinnaxBase/logic/Com_BLE_packets';
import {
  packetIdToName,
  PckCmdIdBeg,
  PckCmdInfo,
  PckRspInfo,
} from '../../../spinnaxBase/logic/Com_BLE_packets';
import { BLCmds, BLRsps, BluetoothEntry } from '../../../types';
import { FSMService, FSMState } from '../../DongleController/DongleController';
import { StatesUserInput } from '../../../pages/Record';
import recordDemoData from './data/Record_demo.json';

interface BLEOriginal {
  scan(services: string[], seconds: number): Observable<any>;
  startScan(services: string[]): Observable<BluetoothEntry>;
  stopScan(): Promise<any>;
  startScanWithOptions(services: string[], options: BLEScanOptions): Observable<any>;
  setPin(pin: string): Observable<any>;
  connect(deviceId: string): Observable<BluetoothEntry>;
  autoConnect(deviceId: string, connectCallback: any, disconnectCallback: any): void;
  requestMtu(deviceId: string, mtuSize: number): Promise<any>;
  refreshDeviceCache(deviceId: string, timeoutMillis: number): Promise<any>;
  disconnect(deviceId: string): Promise<any>;
  read(deviceId: string, serviceUUID: string, characteristicUUID: string): Promise<any>;
  write(deviceId: string, serviceUUID: string, characteristicUUID: string, value: ArrayBuffer): Promise<any>;
  writeWithoutResponse(deviceId: string, serviceUUID: string, characteristicUUID: string, value: ArrayBuffer): Promise<any>;
  startNotification(deviceId: string, serviceUUID: string, characteristicUUID: string): Observable<any>;
  stopNotification(deviceId: string, serviceUUID: string, characteristicUUID: string): Promise<any>;
  isConnected(deviceId: string): Promise<any>;
  isEnabled(): Promise<void>;
  startStateNotifications(): Observable<any>;
  stopStateNotifications(): Promise<any>;
  showBluetoothSettings(): Promise<any>;
  enable(): Promise<any>;
  readRSSI(deviceId: string): Promise<any>;
  connectedPeripheralsWithServices(services: string[]): Promise<any[]>;
  peripheralsWithIdentifiers(uuids: string[]): Promise<any[]>;
  bondedDevices(): Promise<any[]>;
  isLocationEnabled(): Promise<void>;
}

const DEFAULT_CHARACTERISTIC = BLE_SERVICE_SPINNAX + BLE_CHARACTERISTIC_RSP;

//// Mock Data
const MOCK_VERSION = '0.9.6.123';
const MOCK_FW_ID = '2022_05_01_00_00';

// ✅ FIXED: Feste MAC-Adressen für konsistente Tests
const MOCK_DEVICES = [
  {
    name: NAME_FREAK,
    id: 'C5_80_49_F0_C6_35',
    mac: 'C5:80:49:F0:C6:35',
    rssi: -60,
  },
  {
    name: NAME_FREAK + ' #2',
    id: 'AB_CD_EF_01_02_03',
    mac: 'AB:CD:EF:01:02:03',  
    rssi: -50,
  }
];

// Mock Info Setup (unchanged)
const spl = MOCK_VERSION.split('.');
const mockInfo = new PckRspInfo();
mockInfo.dongle_state.version_os_major = Number.parseInt(spl[0]);
mockInfo.dongle_state.version_os_minor = Number.parseInt(spl[1]);
mockInfo.dongle_state.version_os_patch = Number.parseInt(spl[2]);
mockInfo.dongle_state.version_os_build = Number.parseInt(spl[3]);
Array.from(MOCK_VERSION).forEach((_c, idx) => {
  mockInfo.spinnax_freak_version_string[idx] = MOCK_VERSION.charCodeAt(idx);
});
Array.from(MOCK_FW_ID).forEach((_c, idx) => {
  mockInfo.spinnax_freak_datetime_string[idx] = MOCK_FW_ID.charCodeAt(idx);
});

const cmdName = (buf: ArrayBuffer) => {
  const dataView = new DataView(buf);
  const id = dataView.getUint8(0);
  return packetIdToName(id);
};

// Pre-generate all BLE packets from demo data for better performance
const generateAllBLEPackets = () => {
  const samples = recordDemoData.samples;
  console.log(samples);
  const packets: Pck.PckRspReTrg[] = [];

  // Generate packets with 4 samples each
  for (let i = 0; i < samples.length; i += 4) {
    const mockPacket = new Pck.PckRspReTrg();
    
    // Fill the packet with up to 4 samples
    for (let j = 0; j < 4; j++) {
      const sampleIndex = i + j;
      const demoSample = samples[sampleIndex % samples.length]; // Loop if we run out
      
      // ✅ FIXED: Use the existing sample structure from PckRspReTrg
      const packetSample = mockPacket.samples[j];
      
      // Map demo data structure to packet sample fields
      packetSample.t_ms = demoSample.t_ms;
      packetSample.prs_kPa = demoSample.prs_kPa;
      packetSample.tmp_deg_C = demoSample.tmp_deg_C;
      packetSample.mgn = demoSample.mgn;
      
      // Quaternion mapping
      packetSample.q_r = demoSample.q.r;
      packetSample.q_i = demoSample.q.i;
      packetSample.q_j = demoSample.q.j;
      packetSample.q_k = demoSample.q.k;
      
      // Accelerometer mapping
      packetSample.acc_u_ms2 = demoSample.acc_ms2.x;
      packetSample.acc_v_ms2 = demoSample.acc_ms2.y;
      packetSample.acc_w_ms2 = demoSample.acc_ms2.z;
      
      // Gyroscope mapping  
      packetSample.omg_u_rads = demoSample.omg_rads.x;
      packetSample.omg_v_rads = demoSample.omg_rads.y;
      packetSample.omg_w_rads = demoSample.omg_rads.z;
      
      // ✅ DEBUG: Log first few samples to verify structure
      if (i === 0 && j === 0) {
        console.log(`🔍 [BLE Dummy] Sample structure mapping:`, {
          demo: demoSample,
          packet: {
            t_ms: packetSample.t_ms,
            prs_kPa: packetSample.prs_kPa,
            tmp_deg_C: packetSample.tmp_deg_C,
            mgn: packetSample.mgn,
            quaternion: {
              r: packetSample.q_r,
              i: packetSample.q_i,
              j: packetSample.q_j,
              k: packetSample.q_k
            },
            accelerometer: {
              u: packetSample.acc_u_ms2,
              v: packetSample.acc_v_ms2,
              w: packetSample.acc_w_ms2
            },
            gyroscope: {
              u: packetSample.omg_u_rads,
              v: packetSample.omg_v_rads,
              w: packetSample.omg_w_rads
            }
          }
        });
      }
    }
    
    packets.push(mockPacket);
  }

  console.log(`🔧 [BLE Dummy] Generated ${packets.length} packets with mapped demo data`);
  return packets;
};

// Pre-generate all packets for better performance
const preGeneratedPackets = generateAllBLEPackets();

// ✅ FIXED: Create zero packet using existing sample structure
const createZeroPacket = (packetIndex: number): Pck.PckRspReTrg => {
  const zeroPacket = new Pck.PckRspReTrg();
  
  // Get the last demo timestamp to continue from there
  const lastDemoSample = recordDemoData.samples[recordDemoData.samples.length - 1];
  const lastDemoTime = lastDemoSample?.t_ms || 0;
  
  // Fill all 4 samples using existing packet sample structure
  for (let i = 0; i < 4; i++) {
    const packetSample = zeroPacket.samples[i];
    const zeroPacketIndex = packetIndex - preGeneratedPackets.length;
    const zeroSampleIndex = (zeroPacketIndex * 4) + i;
    
    // Continue timeline: last demo time + 10ms for each zero sample
    packetSample.t_ms = lastDemoTime + ((zeroSampleIndex + 1) * 10);
    packetSample.prs_kPa = 101.325; // ✅ Realistic atmospheric pressure
    packetSample.tmp_deg_C = 20.0;  // ✅ Room temperature
    packetSample.mgn = 0;
    
    // Identity quaternion (stationary)
    packetSample.q_r = 1.0;
    packetSample.q_i = 0.0;
    packetSample.q_j = 0.0;
    packetSample.q_k = 0.0;
    
    // Gravity only (stationary)
    packetSample.acc_u_ms2 = 0.0;
    packetSample.acc_v_ms2 = 0.0;
    packetSample.acc_w_ms2 = -9.81;
    
    // No rotation (stationary)
    packetSample.omg_u_rads = 0.0;
    packetSample.omg_v_rads = 0.0;
    packetSample.omg_w_rads = 0.0;
  }
  
  return zeroPacket;
};

// Create mock data generator that goes through packets once, then stops
const createMockDataGenerator = function* () {
  let packetIndex = 0;
  
  while (true) {
    const packet = preGeneratedPackets[packetIndex];
    packetIndex++;
    
    // Removed continuous logging here - it's now handled in sendMockRecordData
    // only when recording is active
    
    yield packet;
  }
};

const mockDataGenerator = createMockDataGenerator();

class BLEMockOriginal implements BLEOriginal {
  private scanObservable?: Subject<BluetoothEntry>;
  private connectedDevices: Record<
    string,
    [BluetoothEntry, ReplaySubject<BluetoothEntry>]
  > = {};
  private characteristics: Record<string, Subject<ArrayBuffer[]>> = {};
  private recordInterval?: any;
  private mockDataInterval?: any;
  private isRecording: boolean = false;
  private sentPacketsCount: number = 0; // Track sent packets
  private readonly maxDemoPackets: number = preGeneratedPackets.length; // Variable packet count
  fsm?: FSMService;

  constructor() {
    this.characteristics[DEFAULT_CHARACTERISTIC] = new Subject<any>();
  }

  // Set FSM reference for checking user input state
  setFSM(fsm: FSMService) {
    this.fsm = fsm;
  }

  scan(_services: string[], seconds: number): Observable<any> {
    const ret = this.startScan(_services);
    setTimeout(() => this.stopScan(), seconds * 1000);
    return ret;
  }

  startScan(_services: string[]): Observable<BluetoothEntry> {
    if (!this.scanObservable) {
      this.scanObservable = new Subject<BluetoothEntry>();

      // ✅ FIXED: Verwende die vordefinierten Mock-Geräte
      MOCK_DEVICES.forEach((device, index) => {
        setTimeout(() => {
          this.scanObservable?.next({
            ...device,
            state: 'disconnected'
          } as BluetoothEntry);
        }, (index + 1) * 1000);
      });
    }

    return this.scanObservable;
  }

  async stopScan(): Promise<any> {
    this.scanObservable?.complete();
    this.scanObservable = undefined;
    return;
  }

  startScanWithOptions(
    services: string[],
    _options: BLEScanOptions
  ): Observable<any> {
    return this.startScan(services);
  }

  setPin(_pin: string): Observable<any> {
    return new Observable((subscriber) => subscriber.next('OK'));
  }

  connect(deviceId: string): Observable<BluetoothEntry> {
    if (!this.connectedDevices[deviceId]) {
      // ✅ FIXED: Finde das Gerät in den Mock-Devices
      const mockDevice = MOCK_DEVICES.find(d => d.id === deviceId);
      
      if (!mockDevice) {
        throw new Error(`Mock device not found: ${deviceId}`);
      }
      
      this.connectedDevices[deviceId] = [
        {
          ...mockDevice,
          state: 'connected',
        },
        new ReplaySubject<BluetoothEntry>(1),
      ];
      
      setTimeout(() => {
        this.connectedDevices[deviceId][1].next(
          this.connectedDevices[deviceId][0]
        );
      }, 1000);
    }

    return this.connectedDevices[deviceId][1];
  }

  autoConnect(
    deviceId: string,
    connectCallback: any,
    disconnectCallback: any
  ): void {
    this.connect(deviceId).subscribe({
      next: connectCallback,
      error: disconnectCallback,
    });
  }

  async requestMtu(_deviceId: string, _mtuSize: number): Promise<any> {
    return;
  }

  refreshDeviceCache(_deviceId: string, _timeoutMillis: number): Promise<any> {
    throw new Error('BLEMock.refreshDeviceCache not implemented');
  }

  async disconnect(deviceId: string): Promise<any> {
    // Stop mock recording when disconnecting
    this.stopMockRecording();
    this.connectedDevices[deviceId]?.[1]?.error(
      this.connectedDevices[deviceId]?.[0]
    );
    delete this.connectedDevices[deviceId];
    return;
  }

  read(
    _deviceId: string,
    _serviceUUID: string,
    _characteristicUUID: string
  ): Promise<any> {
    throw new Error('BLEMock.read not implemented');
  }

  write(
    _deviceId: string,
    _serviceUUID: string,
    _characteristicUUID: string,
    _value: ArrayBuffer
  ): Promise<any> {
    throw new Error('BLEMock.write not implemented');
  }

  sendMockRecordData() {
    // If we're recording but user doesn't want to record anymore, stop recording
    if (this.isRecording && !this.shouldSendData()) {
      this.stopMockRecording();
      return;
    }

    // Only send data if recording is active AND user wants to record
    if (!this.isRecording) {
      return;
    }

    let packet: Pck.PckRspReTrg;
    let isZeroPacket = false;
    
    // Check if we still have demo data or need to send zeros
    if (this.sentPacketsCount < this.maxDemoPackets) {
      // Use real demo data
      packet = preGeneratedPackets[this.sentPacketsCount];
    } else {
      // Send zero packet when demo data runs out
      packet = createZeroPacket(this.sentPacketsCount);
      isZeroPacket = true;
    }
    
    if (!packet) {
      console.error(`No packet available at index ${this.sentPacketsCount}`);
      return;
    }
    
    // Send data to characteristic
    this.characteristics[DEFAULT_CHARACTERISTIC].next([
      packet.toArrayBuffer(),
    ]);
    
    // Increment packet counter
    this.sentPacketsCount++;
    
    // ✅ FIXED: Enhanced logging with packet sample validation
    if (isZeroPacket) {
      const firstSample = packet.samples[0];
      console.log(`⚫ ZERO Packet #${this.sentPacketsCount} - Stationary data`);
      console.log(`   Sample 0: t_ms=${firstSample.t_ms}, prs=${firstSample.prs_kPa} kPa, tmp=${firstSample.tmp_deg_C}°C`);
      console.log(`   Packet Fields: acc_u=${firstSample.acc_u_ms2}, acc_v=${firstSample.acc_v_ms2}, acc_w=${firstSample.acc_w_ms2}`);
      console.log(`   Packet Quaternion: r=${firstSample.q_r}, i=${firstSample.q_i}, j=${firstSample.q_j}, k=${firstSample.q_k}`);
    } else {
      const packetIndex = this.sentPacketsCount - 1;
      const sampleIndex = packetIndex * 4;
      const totalSamples = recordDemoData.samples.length;
      
      console.log(`🚀 Demo Packet #${this.sentPacketsCount}/${this.maxDemoPackets} (samples ${sampleIndex}-${Math.min(sampleIndex+3, totalSamples-1)}/${totalSamples})`);
      
      // Log first sample with packet field validation
      const firstSample = packet.samples[0];
      
      console.log(`   Packet Sample: t_ms=${firstSample.t_ms}, prs=${firstSample.prs_kPa}, tmp=${firstSample.tmp_deg_C}, mgn=${firstSample.mgn}`);
      console.log(`   Packet Accel: u=${firstSample.acc_u_ms2}, v=${firstSample.acc_v_ms2}, w=${firstSample.acc_w_ms2}`);
      console.log(`   Packet Quaternion: r=${firstSample.q_r}, i=${firstSample.q_i}, j=${firstSample.q_j}, k=${firstSample.q_k}`);
      console.log(`   Packet Gyro: u=${firstSample.omg_u_rads}, v=${firstSample.omg_v_rads}, w=${firstSample.omg_w_rads}`);
      
      // ✅ Verify packet sample structure
      const hasAllFields = !!(
        firstSample.t_ms &&
        firstSample.prs_kPa !== undefined &&
        firstSample.acc_u_ms2 !== undefined &&
        firstSample.q_r !== undefined
      );
      
      console.log(`   ${hasAllFields ? '✅' : '❌'} Packet sample has ${hasAllFields ? 'all' : 'missing'} required fields`);
      
      if (this.sentPacketsCount === this.maxDemoPackets) {
        console.log(`🏁 FINAL Demo Packet! Next packets will be stationary data with realistic values.`);
      }
    }
  }

  // Check if user actually wants to record (not just BLE connected)
  private shouldSendData(): boolean {
    if (!this.fsm) {
      return false;
    }

    // Check if user input state is Record (user clicked record button)
    const userInputState = this.fsm.getUserInputState();
    return userInputState === StatesUserInput.Record;
  }

  startMockRecording() {
    if (this.isRecording) {
      // Already recording, don't reset counter - just continue
      return;
    }
    
    this.isRecording = true;
    
    // Counter is now reset in writeWithoutResponse for PckCmdReBeg only
    // Don't reset here to avoid overwriting mid-session counter values
    
    // Send data every 25ms for faster simulation (was 50ms)
    this.mockDataInterval = setInterval(() => {
      this.sendMockRecordData();
    }, 25);
  }

  stopMockRecording() {
    this.isRecording = false;
    
    if (this.mockDataInterval) {
      clearInterval(this.mockDataInterval);
      this.mockDataInterval = undefined;
    }
    
    // Reset packet counter when stopping recording for a clean new start
    this.sentPacketsCount = 0;
    console.log('⏹️ CURRENT VERSION - Recording stopped - Packet counter reset for next session');
  }

  async writeWithoutResponse(
    _deviceId: string,
    _serviceUUID: string,
    _characteristicUUID: string,
    value: ArrayBuffer
  ): Promise<any> {
    const theCmdName = cmdName(value) as keyof BLCmds;
    const theRspName = theCmdName.replace('Cmd', 'Rsp') as keyof BLRsps;

    const ResponseClass = Pck[theRspName];

    const cmd = Pck[theCmdName].ofArrayBuffer(value);
    const answer = () => {
      this.characteristics[DEFAULT_CHARACTERISTIC].next([
        new ResponseClass().toArrayBuffer(),
      ]);
    };

    if (cmd instanceof Pck.PckCmdReBeg) {
      if (this.shouldSendData()) {
        // Reset packet counter only when starting recording from a stopped state
        if (!this.isRecording) {
          this.sentPacketsCount = 0;
          console.log('🔄 CURRENT VERSION - Starting new recording session - Packet counter reset');
        }
        
        this.characteristics[DEFAULT_CHARACTERISTIC].next([
          new Pck.PckRspReBeg().toArrayBuffer(),
        ]);
        this.startMockRecording();
      } else {
        this.characteristics[DEFAULT_CHARACTERISTIC].next([
          new Pck.PckRspIdBeg().toArrayBuffer(),
        ]);
      }
      return;
    } else if (cmd instanceof PckCmdIdBeg) {
      setTimeout(answer, 500);
      this.stopMockRecording();
      return;
    } else if (cmd instanceof PckCmdInfo) {
      setTimeout(
        () =>
          this.characteristics[DEFAULT_CHARACTERISTIC].next([
            mockInfo.toArrayBuffer(),
          ]),
        1000
      );
      return;
    }

    answer();
  }

  startNotification(
    _deviceId: string,
    serviceUUID: string,
    characteristicUUID: string
  ): Observable<any> {
    const key = serviceUUID + characteristicUUID;
    if (!this.characteristics[key]) {
      this.characteristics[key] = new Subject<any>();
    }
    return this.characteristics[key];
  }

  async stopNotification(
    _deviceId: string,
    serviceUUID: string,
    characteristicUUID: string
  ): Promise<any> {
    const key = serviceUUID + characteristicUUID;
    this.characteristics[key]?.complete();
    delete this.characteristics[key];
    return;
  }

  async isConnected(deviceId: string): Promise<any> {
    return !!this.connectedDevices[deviceId];
  }

  isEnabled(): Promise<void> {
    throw new Error('BLEMock.isEnabled not implemented');
  }

  startStateNotifications(): Observable<any> {
    throw new Error('BLEMock.startStateNotifications not implemented');
  }

  stopStateNotifications(): Promise<any> {
    throw new Error('BLEMock.stopStateNotifications not implemented');
  }

  showBluetoothSettings(): Promise<any> {
    throw new Error('BLEMock.showBluetoothSettings not implemented');
  }

  enable(): Promise<any> {
    throw new Error('BLEMock.enable not implemented');
  }

  readRSSI(_deviceId: string): Promise<any> {
    throw new Error('BLEMock.readRSSI not implemented');
  }

  async connectedPeripheralsWithServices(_services: string[]): Promise<any[]> {
    return MOCK_DEVICES.map(device => ({
      ...device,
      state: 'disconnected'
    }));
  }

  peripheralsWithIdentifiers(_uuids: string[]): Promise<any[]> {
    throw new Error('BLEMock.peripheralsWithIdentifiers not implemented');
  }

  bondedDevices(): Promise<any[]> {
    throw new Error('BLEMock.bondedDevices not implemented');
  }

  isLocationEnabled(): Promise<void> {
    throw new Error('BLEMock.isLocationEnabled not implemented');
  }
}

const BLEMock = new BLEMockOriginal();

export const demoDongle = mockInfo;
export default BLEMock;
